import ChangelogModal from '@app/features/changelog/ChangelogModal';
import {
  type ChangelogEntry,
  getCurrentTagVersion,
  getLastSeenChangelogTag,
  getNewChangelogEntries,
  isChangelogModalEnabled,
  setLastSeenChangelogTag,
  sortChangelogEntries,
} from '@app/features/changelog/changelog.utils';
import React, { createContext, useContext, useEffect, useState } from 'react';

type ChangelogContextValue = {
  openFullChangelog: () => void;
};

const ChangelogContext = createContext<ChangelogContextValue | undefined>(undefined);

type ChangelogProviderProps = {
  children: React.ReactNode;
};

export const ChangelogProvider = ({ children }: ChangelogProviderProps) => {
  const [allEntries, setAllEntries] = useState<ChangelogEntry[]>([]);
  const [visibleEntries, setVisibleEntries] = useState<ChangelogEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'latest' | 'full'>('latest');

  useEffect(() => {
    let isActive = true;

    const loadChangelog = async () => {
      try {
        const response = await fetch('/changelog.json', { cache: 'no-store' });

        if (!response.ok) {
          throw new Error(`Failed to load changelog: ${response.status}`);
        }

        const data = (await response.json()) as ChangelogEntry[];
        const sortedEntries = sortChangelogEntries(data);

        if (!isActive) {
          return;
        }

        setAllEntries(sortedEntries);

        // The deployment can switch the automatic popup off. The entries are still
        // loaded, so View Changelog works, and the last-seen tag is deliberately left
        // alone: turning the popup back on should then show what the release changed.
        if (!isChangelogModalEnabled()) {
          return;
        }

        const currentTag = getCurrentTagVersion();
        const lastSeenTag = getLastSeenChangelogTag();
        const newEntries = getNewChangelogEntries(sortedEntries, currentTag, lastSeenTag);

        if (lastSeenTag !== currentTag) {
          setLastSeenChangelogTag(currentTag);
        }

        if (newEntries.length > 0) {
          setVisibleEntries(newEntries);
          setMode('latest');
          setIsOpen(true);
        }
      } catch (error) {
        console.warn('Unable to load changelog', error);
      }
    };

    void loadChangelog();

    return () => {
      isActive = false;
    };
  }, []);

  const closeModal = () => {
    setIsOpen(false);
  };

  const openFullChangelog = () => {
    setVisibleEntries(allEntries);
    setMode('full');
    setIsOpen(true);
  };

  const viewFullHistoryFromLatest = () => {
    setVisibleEntries(allEntries);
    setMode('full');
  };

  return (
    <ChangelogContext.Provider value={{ openFullChangelog }}>
      {children}
      <ChangelogModal
        entries={visibleEntries}
        mode={mode}
        onClose={closeModal}
        onViewFullHistory={mode === 'latest' ? viewFullHistoryFromLatest : undefined}
        open={isOpen}
      />
    </ChangelogContext.Provider>
  );
};

export const useChangelog = () => {
  const context = useContext(ChangelogContext);

  if (!context) {
    throw new Error('useChangelog must be used within a ChangelogProvider');
  }

  return context;
};
